const present = value => value !== undefined && value !== null && value !== '';

const aliases = {
  log_hr: ['log_hr', 'loghr', 'logHR'], z: ['z'],
  p_value: ['p_value', 'p'], hazard_ratio: ['hazard_ratio', 'hr'],
  ci_95: ['ci_95'], ci_lower: ['ci_lower', 'lo'], ci_upper: ['ci_upper', 'hi'], estimate: ['estimate'], std_error: ['std_error', 'se'],
  statistic: ['statistic', 'stat'], seer_version: ['seer_version', 'see_rversion'],
};

const sources = {
  categorical: ['categorical', 'cat'], continuous: ['continuous', 'cont'],
  projection: ['population_projection', 'projection', 'proj'],
};

// Only place flat Cox metrics in a model group when the response identifies that model.
export const getPrognosisMetric = (row, group, key, mode) => {
  const keys = aliases[key] || [key];
  const names = sources[group] || [];
  for (const name of names) {
    for (const field of keys) {
      const value = row[name]?.[field] ?? row[`${name}_${field}`] ?? row[`${field}_${name}`];
      if (present(value)) return value;
    }
  }
  const coxType = String(row.cox_type || row.coxType || '').toLowerCase();
  const isFlatSource = group === 'metadata' ||
    (group === 'categorical' && ['categorical', 'cat'].includes(coxType)) ||
    (group === 'continuous' && ['continuous', 'cont'].includes(coxType)) ||
    (group === 'projection' && ['population', 'concordant'].includes(mode));
  if (isFlatSource || (group === 'projection' && key === 'seer_version')) {
    for (const field of keys) if (present(row[field])) return row[field];
  }
  if (key === 'ci_95') {
    const lower = getPrognosisMetric(row, group, 'ci_lower', mode);
    const upper = getPrognosisMetric(row, group, 'ci_upper', mode);
    if (present(lower) && present(upper)) return `${lower} – ${upper}`;
  }
  return null;
};

export const formatPrognosisMetric = value => {
  if (!present(value)) return '—';
  const number = Number(value);
  if (!Number.isFinite(number)) return String(value);
  if (number !== 0 && Math.abs(number) < 0.0001) return number.toExponential(2);
  return number.toFixed(4);
};

const metric = (key, label, group, mode) => ({
  id: `${group}-${key}`, label, group,
  value: row => getPrognosisMetric(row, group, key, mode),
  format: key === 'seer_version' || key === 'ci_95' ? value => value ?? '—' : formatPrognosisMetric,
  width: key === 'ci_95' ? 170 : key === 'seer_version' ? 130 : 112,
});

export const getPrognosisTableGroups = (rows, mode) => {
  const cox = group => ['p_value', 'hazard_ratio', 'ci_95'].map((key, i) => metric(key, ['p-value', 'HR', '95% CI'][i], group, mode));
  const groups = [];
  if (mode !== 'population') {
    groups.push({ id: 'categorical', label: 'Categorical Cox model', columns: cox('categorical') });
    groups.push({ id: 'continuous', label: 'Continuous Cox model', columns: cox('continuous') });
  }
  if (mode !== 'cohort') {
    groups.push({ id: 'projection', label: 'Population projection', columns: ['p_value', 'estimate', 'std_error', 'statistic', 'seer_version'].map((key, i) => metric(key, ['p-value', 'Estimate', 'Std. error', 'Statistic', 'SEER version'][i], 'projection', mode)) });
  }
  return groups;
};

export const getPrognosisContextColumns = (mode, categoryLabel) => [
  { id: 'gene_name', label: 'Gene name', width: 140 },
  { id: 'cohort', label: 'Cohort', width: 110 },
  { id: 'data_level', label: 'Data level', width: 125 },
  { id: 'histological_subtype', label: 'Histological subtype', width: 160 },
  { id: 'category', label: categoryLabel || (mode === 'concordant' ? 'Concordant category' : 'Category'), width: 185 },
].map(column => ({ ...column, value: row => column.id === 'category' ? (row.concordant_category ?? row.category) : row[column.id] }));
